import * as React from 'react';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState
} from 'react';
import { useHistory } from 'react-router-dom';
import { Paths } from '../../../App/routes';
import { NEW_TICKET_ID } from '../../constants/newTicketId';
import { ActionScenarioType } from '../../telemetry/Telemetry.constants';
import { TelemetryScenarioTracker } from '../../telemetry/TelemetryScenarioTracker';
import { TelemetryScenarioName } from '../../telemetry/TelemetryScenarioTracker.types';
import {
  LoadContext,
  PageLoadConstants,
  PageLoadContextType
} from './page-load-context.types';

const PageLoadContext = createContext<PageLoadContextType>({
  loadContext: {},
  setLoadContext: () => undefined
});

export const PageLoadContextProvider: React.FC = ({ children }) => {
  const [loadContext, setLoadContext] = useState<LoadContext>({});
  const history = useHistory();

  useEffect(() => {
    history.listen(location => {
      // Whenever path changes, we need to start fresh
      setLoadContext({
        [location.pathname as keyof typeof Paths]: PageLoadConstants.NotLoaded
      });

      // And log page load telemetry
      startPageLoad(location.pathname);
    });
  }, [history]);

  return (
    <PageLoadContext.Provider value={{ loadContext, setLoadContext }}>
      {children}
    </PageLoadContext.Provider>
  );
};

/**
 * Returns loadState and a function to setLoaded for the path given by `name`
 * @param name name of the `Path`
 */
export const useLoadedContext = (
  name: Paths
): {
  loadState: PageLoadConstants;
  setLoaded: () => void;
} => {
  const nameRef = useRef<Paths>(name);
  const { loadContext, setLoadContext } = useContext(PageLoadContext);
  const setLoadContextToLoaded = useCallback(() => {
    if (loadContext[nameRef.current] !== PageLoadConstants.Loaded) {
      setLoadContext({ [nameRef.current]: PageLoadConstants.Loaded });
    }
  }, [loadContext, setLoadContext]);

  return {
    loadState: loadContext[nameRef.current] || PageLoadConstants.NotLoaded,
    setLoaded: setLoadContextToLoaded
  };
};

/**
 * Runs a function when a specific path gets "loaded" state in PageLoadContext
 * @param effect function to run when above condition is met
 * @param name Path name for which the effect needs to be run
 */
export function usePageLoadEffect(effect: () => void, name: Paths): void {
  const effectRef = useRef<typeof effect>(effect);
  const nameRef = useRef<Paths>(name);
  const { loadContext, setLoadContext } = useContext(PageLoadContext);

  // this should not be necessary since this is a setState but somehow
  // TS does not see it yet as a unchanging reference for some reason
  const setLoadContextRef = useRef<typeof setLoadContext>(setLoadContext);

  useEffect(() => {
    const specificLoadContext = loadContext[nameRef.current];
    if (specificLoadContext === PageLoadConstants.Loaded) {
      if (effectRef.current && setLoadContextRef.current) {
        effectRef.current();
      }
    }
  }, [loadContext]);
}

/**
 * Begins tracking page loading, runs telemetry scenario tracker for page load scenario.
 * @param pagePath - route of the page.
 */
export const startPageLoad = (pagePath: string): void => {
  switch (pagePath) {
    case Paths.EmailGetStarted:
      TelemetryScenarioTracker.start(
        TelemetryScenarioName.PageLoad,
        ActionScenarioType.GetStartedWizard
      );
      break;
    case Paths.EmailTicketListTickets:
      TelemetryScenarioTracker.start(
        TelemetryScenarioName.PageLoad,
        ActionScenarioType.Messages
      );
      break;
    default:
      if (pagePath.startsWith(Paths.EmailTicketDetails.replace(':id', ''))) {
        let ticketId = pagePath.substring(
          Paths.EmailTicketDetails.replace(':id', '').length
        );

        TelemetryScenarioTracker.start(
          TelemetryScenarioName.PageLoad,
          ActionScenarioType.MessageDetails,
          ticketId === NEW_TICKET_ID ? 'New ticket' : 'Existing ticket'
        );
      }
      break;
  }
};
/**
 * Ends tracking page load. Logs indicators to the telemetry scenario tracker.
 * @param scenarioType - string value for the page panel loaded.
 * @param error        - Error received suring loading the page.
 * @param message      - error message if any.
 */
export const finishPageLoad = (
  scenarioType: string,
  error?: Error,
  message?: string
): void => {
  TelemetryScenarioTracker.finish(
    TelemetryScenarioName.PageLoad,
    scenarioType,
    error,
    message
  );
};
